import { useState } from 'react';
import {
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    Button,
    Chip,
    IconButton,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PageHeader } from '../components/PageHeader';
import { icontactApi, IContactFolderContactInfo } from '../services/icontact-api.service';

export function IContactPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<IContactFolderContactInfo[] | null>(null);
    const [selectedContact, setSelectedContact] = useState<IContactFolderContactInfo | null>(null);

    const handleSearch = async () => {
        if (!email.trim()) {
            setError('Please enter an email address');
            return;
        }

        setLoading(true);
        setError(null);
        setResults(null);
        setSelectedContact(null);

        try {
            const result = await icontactApi.searchContact(email.trim());
            if (result.success && result.data) {
                setResults(result.data.contacts);
            } else {
                setError(result.error || 'Failed to search contact');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to search contact');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleShowContactDetails = (folderInfo: IContactFolderContactInfo) => {
        setSelectedContact(folderInfo);
    };

    const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
        switch (status) {
            case 'normal':
                return 'success';
            default:
                return 'warning';
        }
    };

    // Custom label mapping for contact fields
    const fieldLabelMap: Record<string, string> = {
        contactId: 'Contact ID',
        currentage: 'Current Age',
        dateofbirth: 'Date Of Birth',
        dateofinstruction: 'Date Of Instruction',
        internalid: 'Internal ID',
        pipelinestatus: 'Pipeline Status',
        salesrep: 'Sales Rep',
        tmstatus: 'TM Status',
        tmcentre: 'TM Centre',
    };

    const getFieldLabel = (key: string): string => {
        // Check custom mapping first (case-insensitive)
        const lowerKey = key.toLowerCase();
        for (const [mapKey, label] of Object.entries(fieldLabelMap)) {
            if (mapKey.toLowerCase() === lowerKey) {
                return label;
            }
        }
        // Fallback: convert camelCase to Title Case
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    return (
        <PageHeader currentPath="/icontact">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <EmailIcon sx={{ fontSize: 28, mt: 0.3, mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h5" component="h1" fontWeight={600}>
                    iContact
                </Typography>
            </Box>

            {/* Search Section */}
            <Box sx={{ mt: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                        label="Email Address"
                        variant="outlined"
                        size="small"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={{ flexGrow: 1, maxWidth: 300 }}
                        placeholder="Enter email address"
                        disabled={loading}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        Search
                    </Button>
                </Box>
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Results Section */}
            {results !== null && (
                <Paper sx={{ p: 1, width: 'fit-content' }}>
                    {results.length === 0 ? (
                        <Alert severity="info">No contact found with this email address in any folder.</Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Folder</TableCell>
                                        <TableCell>Contact Status</TableCell>
                                        <TableCell>Bounces</TableCell>
                                        <TableCell>List Name</TableCell>
                                        <TableCell>Subscription Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.flatMap((folderInfo) =>
                                        folderInfo.subscriptions.length > 0
                                            ? folderInfo.subscriptions.map((sub, index) => (
                                                  <TableRow key={`${folderInfo.folderId}-${sub.subscriptionId}`}>
                                                      <TableCell>
                                                          {index === 0 && (
                                                              <Box
                                                                  sx={{
                                                                      display: 'flex',
                                                                      alignItems: 'center',
                                                                      gap: 0.5,
                                                                  }}
                                                              >
                                                                  <Typography
                                                                      component="span"
                                                                      sx={{ cursor: 'pointer' }}
                                                                      onClick={() =>
                                                                          handleShowContactDetails(folderInfo)
                                                                      }
                                                                  >
                                                                      {folderInfo.folderName}
                                                                  </Typography>
                                                                  <IconButton
                                                                      size="small"
                                                                      onClick={() =>
                                                                          handleShowContactDetails(folderInfo)
                                                                      }
                                                                      sx={{ p: 0.25 }}
                                                                  >
                                                                      <InfoOutlinedIcon fontSize="small" />
                                                                  </IconButton>
                                                              </Box>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          {index === 0 && folderInfo.contact.status ? (
                                                              <Chip
                                                                  label={folderInfo.contact.status}
                                                                  size="small"
                                                                  variant="outlined"
                                                                  color={getStatusColor(folderInfo.contact.status)}
                                                              />
                                                          ) : null}
                                                      </TableCell>
                                                      <TableCell>
                                                          {index === 0 && folderInfo.contact.bounceCount
                                                              ? folderInfo.contact.bounceCount
                                                              : ''}
                                                      </TableCell>
                                                      <TableCell>{sub.listName}</TableCell>
                                                      <TableCell>
                                                          <Chip
                                                              label={sub.status}
                                                              size="small"
                                                              variant="outlined"
                                                              color={getStatusColor(sub.status)}
                                                          />
                                                      </TableCell>
                                                  </TableRow>
                                              ))
                                            : [
                                                  <TableRow key={folderInfo.folderId}>
                                                      <TableCell>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                              <Typography
                                                                  component="span"
                                                                  sx={{ cursor: 'pointer' }}
                                                                  onClick={() => handleShowContactDetails(folderInfo)}
                                                              >
                                                                  {folderInfo.folderName}
                                                              </Typography>
                                                              <IconButton
                                                                  size="small"
                                                                  onClick={() => handleShowContactDetails(folderInfo)}
                                                                  sx={{ p: 0.25 }}
                                                              >
                                                                  <InfoOutlinedIcon fontSize="small" />
                                                              </IconButton>
                                                          </Box>
                                                      </TableCell>
                                                      <TableCell>
                                                          {folderInfo.contact.status && (
                                                              <Chip
                                                                  label={folderInfo.contact.status}
                                                                  size="small"
                                                                  variant="outlined"
                                                              />
                                                          )}
                                                      </TableCell>
                                                      <TableCell>{folderInfo.contact.bounceCount || ''}</TableCell>
                                                      <TableCell colSpan={2}>
                                                          <Typography variant="body2" color="text.secondary">
                                                              No subscriptions
                                                          </Typography>
                                                      </TableCell>
                                                  </TableRow>,
                                              ],
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* Contact Details Box */}
            {selectedContact && (
                <Paper sx={{ p: 2, mt: 2, maxWidth: 500 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Contact Details - {selectedContact.folderName}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, alignItems: 'baseline' }}>
                        {Object.entries(selectedContact.contact)
                            .filter(([, value]) => value !== undefined && value !== null && value !== '')
                            .map(([key, value]) => {
                                const label = getFieldLabel(key);

                                // Format value based on type
                                let displayValue: string;
                                if (key.toLowerCase().includes('date') && typeof value === 'string') {
                                    displayValue = new Date(value).toLocaleDateString();
                                } else {
                                    displayValue = String(value);
                                }

                                return (
                                    <Box key={key} sx={{ display: 'contents' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {label}:
                                        </Typography>
                                        <Typography variant="body2">{displayValue}</Typography>
                                    </Box>
                                );
                            })}
                    </Box>
                </Paper>
            )}
        </PageHeader>
    );
}
